import React, { useState, useContext, useEffect } from 'react';
import useAsyncEffect from 'use-async-effect';
import AppContext from 'contexts/appContext';
import Panel from 'components/common/panel/panel';
import BoardingService from 'services/boardingService';
import moment from 'moment';

export default function BoardingOrderDetailsPage(props) {
  const { setAppArea, setAppBreadcrumb } = useContext(AppContext);

  const id = props.match.params.id;

  const [details, setDetails] = useState(null);
  const [workflow, setWorkflow] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setAppArea(AppContext.Areas.Boarding, 'Boarding');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Boarding' }, { name: 'Order Status', selected: true }]);
  }, []);

  useAsyncEffect(async () => {
    setDetails(await BoardingService.API.getOrderDetails(id));
    setWorkflow(await BoardingService.API.getOrderWorkflow(id));
    setLoaded(true);
  }, []);

  const renderApplicationsList = () => {
    const list = [];
    if (details.orderedReporting) {
      list.push(<div key='1'><div className='form-label'>+</div>&nbsp;POSiCloud Reporting</div>);
    }
    if (list.length === 0) {
      return 'None';
    }
    if (details.isTrial) {
      list.push(<div key='0'><span className='badge badge-primary'>Free Trial</span></div>);
    }
    return list;
  };

  if (!loaded) {
    return null;
  }
  return (
    <div className='row justify-content-md-center mx-0 mt-4'>
      <div className='col-12 col-md-10 col-lg-4 col-xl-3'>
        <Panel>
          <Panel.Header>Order Details</Panel.Header>
          <Panel.Body>
            <div className='mb-2'>
              <div><div className='form-label'>Application</div></div>
              {renderApplicationsList()}
            </div>
            <div className='mb-2'>
              <div><div className='form-label'>Site</div></div>
              <div>{details.storeName}</div>
            </div>
            <div className='mb-2'>
              <div><div className='form-label'>Key Number</div></div>
              <div>{details.keyId}</div>
            </div>
            <div className='mb-2'>
              <div><div className='form-label'>Enterprise</div></div>
              <div>{details.enterpriseName}</div>
            </div>
            <div className='mb-2'>
              <div><div className='form-label'>User</div></div>
              <div>{details.userFirstName} {details.userLastName}</div>
              <div className='text-gray'>{details.userEmail}</div>
            </div>
            <div className='mb-2'>
              <div><div className='form-label'>Dealership</div></div>
              <div>{details.dealerName}</div>
            </div>
            <div className='mb-2'>
              <div><div className='form-label'>Special Instructions</div></div>
              <div className='subtle'>{details.notes || 'None'}</div>
            </div>
            <div className='status small'>
              <div>Submitted {moment(details.created).format('dddd, MMMM D, YYYY h:mma')}</div>
            </div>
          </Panel.Body>
        </Panel>
      </div>
      <div className='col-12 col-md-10 col-lg-7 col-xl-8'>
        <WorkflowPanel data={workflow} stepsCount={details.stepsCount} stepsCompletedCount={details.stepsCompletedCount} />
      </div>
    </div>
  );
}

function WorkflowPanel(props) {
  const data = props.data || [];

  const renderProgressBlocks = () => {
    const blocks = [];
    let color = '#23c6c8';
    for (let i = 0; i < props.stepsCount; i++) {
      if (i >= 7) {
        color = '#1ab394';
      }
      blocks.push(<div className='progress-block' style={{ backgroundColor: (i < props.stepsCompletedCount) ? color : null }} key={i}></div>);
    }
    return blocks;
  };

  return (
    <Panel>
      <Panel.Header>Workflow</Panel.Header>
      {
        data.length === 0 ?
          <Panel.Body>Progress has not started yet. Please check back at a later time.</Panel.Body>
          :
          <Panel.Body>
            <div><div className='form-label'>Steps Completed</div> {props.stepsCompletedCount}/{props.stepsCount}</div>
            <div className='progress-blocks progress-blocks-lg mt-2'>
              {renderProgressBlocks()}
            </div>
          </Panel.Body>
      }
      {
        data.map((step, i) => <WorkflowStep data={step} key={i} />)
      }
    </Panel >
  );
}

function WorkflowStep(props) {
  const data = props.data || {};

  const getStepName = () => {
    switch (data.number) {
      case 0: //Not Started
        return 'Not Started';
      case 1: //Create Enterprise
        return 'Create Enterprise';
      case 2: //Create Store
        return 'Create Store';
      case 3: //Wait for Agent Connectivity
        return 'Wait for Agent Connectivity';
      case 4: //Register Site
        return 'Register Site';
      case 5: //Activate Licenses
        return 'Activate Licenses';
      case 6: //Wait for Confirmation of Data
        return 'Wait for Confirmation of Data';
      case 7: //Create User
        return 'Create User';
      case 8: //Wait for User to Accept Invite
        return 'Wait for User to Accept Invite';
      case 9: //Start Trial
        return 'Start Trial';
      default:
        return 'Unknown';
    }
  };

  const getColorClass = () => {
    switch (data.statusId) {
      case -2: //Error
        return 'bg-light-danger';
      case -1: //Skipped
        return '';
      case 0: //Pending
        return '';
      case 1: //In Progress
        return 'bg-light-primary';
      case 2: //Success
        return 'bg-light-success';
      default:
        return '';
    }
  };

  const renderProgressBadge = () => {
    switch (data.statusId) {
      case -2: //Error
        return (
          <span className='badge badge-danger'>
            <i className='icons8-box-important' /> Error
          </span>
        );
      case -1: //Skipped
        return (
          <span className='badge badge-default'>
            <i className='icons8-downward-arrow' /> Skipped
          </span>
        );
      case 0: //Pending
        return (
          <span className='badge badge-default'>
            <i className='icons8-clock' /> Pending
          </span>
        );
      case 1: //In Progress
        return (
          <span className='badge badge-primary'>
            <i className='icons8-submit-progress' /> In Progress
          </span>
        );
      case 2: //Success
        return (
          <span className='badge badge-success'>
            <i className='icons8-checkmark' /> Success
          </span>
        );
      default:
        return null;
    }
  };

  const renderNotes = () => {
    if (data.notes) {
      return data.notes.split('\n').map((v, i) => <div key={i}>{v}</div>);
    }
    return null;
  };

  const renderResults = () => {
    if (data.notes && data.notes.length > 0) {
      switch (data.statusId) {
        case -2: //Error
          return (
            <div className='mx-3 mt-2'>
              <div className='text-danger'>{renderNotes()}</div>
            </div>
          );
        case -1: //Skipped
          return null;
        case 0: //Pending
          return null;
        case 1: //In Progress
          return (
            <div className='mx-3 mt-2'>
              {renderNotes()}
            </div>
          );
        case 2: //Success
          return (
            <div className='mx-3 mt-2'>
              {renderNotes()}
            </div>
          );
        default:
          return null;
      }
    }
    return null;
  };

  return (
    <Panel.Body className={getColorClass()}>
      <div className='d-flex align-items-center'>
        <span className='badge badge-outline-default badge-pill mr-2'>{data.order}</span>
        <h4 className='text-thin mt-2 mr-3'>{getStepName()}</h4>
        {renderProgressBadge()}
      </div>
      {renderResults()}
    </Panel.Body>
  );
}